import PDFBuilder, { fmtDate, fmtDateFromISO, formatCI } from "../../utils/pdfBuilder.js";
import { CONF_OPTIONS } from "./steps/DetaliiStep";

const durataObligatieLabels = { "1": "1 (un) an", "2": "2 (doi) ani", "3": "3 (trei) ani", "5": "5 (cinci) ani" };
const durataAcordLabels = { colaborare: "pe durata colaborarii dintre Parti", "1": "1 (un) an de la data semnarii", "2": "2 (doi) ani de la data semnarii", nedeterminata: "pe durata nedeterminata" };
const instantaLabels = {
  dezvaluie: "instantele judecatoresti competente de la sediul/domiciliul Partii care dezvaluie",
  primeste: "instantele judecatoresti competente de la sediul/domiciliul Partii care primeste",
  bucuresti: "Tribunalul Bucuresti",
};

function getPartyName(data, prefix) {
  if (data[`${prefix}Tip`] === "pf") return data[`${prefix}Nume`] || "________________________";
  if (data[`${prefix}Tip`] === "pfa") return data[`${prefix}Denumire`] || "________________________";
  if (data[`${prefix}Tip`] === "srl") return data[`${prefix}Denumire`] || "________________________";
  return "________________________";
}

function renderParty(pdf, data, prefix) {
  const tip = data[`${prefix}Tip`];
  if (tip === "pf") {
    pdf.field("Nume", data[`${prefix}Nume`]);
    pdf.field("Domiciliul", data[`${prefix}Adresa`]);
    if (data[`${prefix}CNP`]) pdf.field("CNP", data[`${prefix}CNP`]);
    if (data[`${prefix}CI`]) {
      pdf.pMixed([
        { text: "CI seria " },
        { text: formatCI(data[`${prefix}CI`]), bold: true },
      ], { indent: 5 });
    }
  } else if (tip === "pfa") {
    pdf.pMixed([
      { text: "PFA " },
      { text: data[`${prefix}Denumire`] || "________________________", bold: true },
      { text: ", CUI " },
      { text: data[`${prefix}CUI`] || "________________________", bold: true },
      { text: ", cu sediul profesional in " },
      { text: data[`${prefix}Sediu`] || "________________________", bold: true },
    ], { indent: 5 });
    pdf.pMixed([
      { text: "Reprezentat prin " },
      { text: data[`${prefix}Titular`] || "________________________", bold: true },
      ...(data[`${prefix}CNP`] ? [{ text: ", CNP " }, { text: data[`${prefix}CNP`], bold: true }] : []),
    ], { indent: 5 });
  } else if (tip === "srl") {
    pdf.field("Denumire", data[`${prefix}Denumire`]);
    pdf.field("CUI", data[`${prefix}CUI`]);
    if (data[`${prefix}RegCom`]) pdf.field("Nr. Reg. Com.", data[`${prefix}RegCom`]);
    pdf.field("Sediu social", data[`${prefix}Sediu`]);
    pdf.field("Reprezentant legal", data[`${prefix}Reprezentant`]);
  }
}

function getConfidentialItems(data) {
  const items = [];
  CONF_OPTIONS.forEach((opt) => {
    if (data[opt.key]) items.push(opt.label.toLowerCase());
  });
  if (data.confAltele && data.confAlteleText) items.push(data.confAlteleText.toLowerCase());
  return items;
}

export default function buildNDAPDF(data, filename) {
  const pdf = new PDFBuilder();
  const signDate = data.dataSemnare ? fmtDateFromISO(data.dataSemnare) : fmtDate(new Date());

  const p1Label = data.bilateral ? "Prima Parte" : "Partea care dezvaluie";
  const p2Label = data.bilateral ? "A Doua Parte" : "Partea care primeste";

  // Header
  pdf.title("Acord de Confidentialitate (NDA)", `Nr. ______ din ${signDate}`);

  // I. PARTILE
  pdf.section("I. PARTILE");

  pdf.pMixed([{ text: `1.1. ${p1Label}:`, bold: true }]);
  renderParty(pdf, data, "partea1");

  pdf.italic("si");

  pdf.pMixed([{ text: `1.2. ${p2Label}:`, bold: true }]);
  renderParty(pdf, data, "partea2");

  pdf.p('denumite in continuare individual "Partea" si colectiv "Partile".', { size: 9 });

  // II. SCOPUL ACORDULUI
  pdf.section("II. SCOPUL ACORDULUI");
  pdf.pMixed([
    { text: "2.1. ", bold: true },
    { text: "Prezentul acord reglementeaza protectia informatiilor confidentiale transmise intre Parti in cadrul: " },
    { text: data.scop || "________________________", bold: true },
    { text: "." },
  ]);

  // III. DEFINITIA INFORMATIILOR CONFIDENTIALE
  pdf.section("III. DEFINITIA INFORMATIILOR CONFIDENTIALE");
  pdf.pMixed([
    { text: "3.1. ", bold: true },
    { text: 'Prin "Informatii Confidentiale" se inteleg toate informatiile, datele, documentele si materialele transmise de o Parte celeilalte, in orice forma (scrisa, verbala, electronica sau vizuala), inclusiv dar fara a se limita la:' },
  ]);

  const confItems = getConfidentialItems(data);
  confItems.forEach((item, i) => {
    pdf.letterItem("abcdefghij"[i], item);
  });

  pdf.pMixed([
    { text: "3.2. ", bold: true },
    { text: "Nu sunt considerate Informatii Confidentiale:" },
  ]);
  pdf.letterItem("a", "informatiile care sunt sau devin publice fara culpa Partii care le primeste");
  pdf.letterItem("b", "informatiile care erau deja in posesia Partii care le primeste inainte de divulgare");
  pdf.letterItem("c", "informatiile obtinute in mod independent de Partea care le primeste din surse terte care nu au obligatie de confidentialitate");
  pdf.letterItem("d", "informatiile a caror divulgare este impusa prin lege sau de o autoritate competenta");

  // IV. OBLIGATIILE PARTILOR
  pdf.section("IV. OBLIGATIILE PARTILOR");

  if (data.bilateral) {
    pdf.pMixed([{ text: "4.1. ", bold: true }, { text: "Fiecare Parte se obliga:" }]);
    pdf.letterItem("a", "sa utilizeze Informatiile Confidentiale primite exclusiv in scopul mentionat la art. 2.1");
    pdf.letterItem("b", "sa nu divulge Informatiile Confidentiale ale celeilalte Parti catre terti fara acordul scris prealabil");
    pdf.letterItem("c", "sa protejeze Informatiile Confidentiale primite cu cel putin acelasi grad de protectie pe care il aplica propriilor informatii confidentiale");
    pdf.letterItem("d", "sa limiteze accesul la Informatiile Confidentiale doar la persoanele care au nevoie de aceste informatii pentru realizarea scopului acordului");
    pdf.letterItem("e", "sa informeze imediat cealalta Parte in cazul oricarei divulgari neautorizate");
  } else {
    pdf.pMixed([{ text: "4.1. ", bold: true }, { text: "Partea care primeste se obliga:" }]);
    pdf.letterItem("a", "sa utilizeze Informatiile Confidentiale exclusiv in scopul mentionat la art. 2.1");
    pdf.letterItem("b", "sa nu divulge Informatiile Confidentiale catre terti fara acordul scris prealabil al Partii care dezvaluie");
    pdf.letterItem("c", "sa protejeze Informatiile Confidentiale cu cel putin acelasi grad de protectie pe care il aplica propriilor informatii confidentiale");
    pdf.letterItem("d", "sa limiteze accesul la Informatiile Confidentiale doar la persoanele care au nevoie de aceste informatii pentru realizarea scopului acordului");
    pdf.letterItem("e", "sa informeze imediat Partea care dezvaluie in cazul oricarei divulgari neautorizate");
  }

  // V. DURATA
  pdf.section("V. DURATA");
  pdf.pMixed([
    { text: "5.1. ", bold: true },
    { text: "Prezentul acord intra in vigoare la data semnarii si este valabil " },
    { text: durataAcordLabels[data.durataAcord] || "pe durata colaborarii dintre Parti", bold: true },
    { text: "." },
  ]);
  pdf.pMixed([
    { text: "5.2. ", bold: true },
    { text: "Obligatia de confidentialitate se mentine pentru o perioada de " },
    { text: durataObligatieLabels[data.durataObligatie] || "2 (doi) ani", bold: true },
    { text: " de la incetarea prezentului acord." },
  ]);

  // VI. RESTITUIREA INFORMATIILOR
  pdf.section("VI. RESTITUIREA INFORMATIILOR");
  pdf.pMixed([
    { text: "6.1. ", bold: true },
    { text: "La incetarea prezentului acord sau la cererea Partii care a dezvaluit informatiile, cealalta Parte se obliga sa restituie sau sa distruga toate materialele continand Informatii Confidentiale, inclusiv copiile acestora, in termen de 15 zile." },
  ]);

  // VII. RASPUNDERE
  pdf.section("VII. RASPUNDERE");
  pdf.pMixed([
    { text: "7.1. ", bold: true },
    { text: "Fiecare Parte raspunde pentru prejudiciile cauzate celeilalte Parti prin incalcarea obligatiilor asumate prin prezentul acord." },
  ]);

  // Dynamic section numbering
  let sectionNum = 8;
  let articleNum = 8;

  // VII. PENALITATI (conditional)
  if (data.penalitati) {
    const roman = toRoman(sectionNum);
    pdf.section(`${roman}. PENALITATI`);
    pdf.pMixed([
      { text: `${articleNum}.1. `, bold: true },
      { text: "Incalcarea obligatiilor de confidentialitate prevazute in prezentul acord atrage obligatia Partii in culpa de a plati celeilalte Parti daune-interese in cuantum de " },
      { text: `${data.penalitateSuma || "___"} ${data.penalitateMoneda || "EUR"}`, bold: true },
      { text: "." },
    ]);
    pdf.pMixed([
      { text: `${articleNum}.2. `, bold: true },
      { text: "Plata penalitatilor nu exonereaza Partea in culpa de obligatia de a repara integral prejudiciul cauzat prin incalcarea obligatiei de confidentialitate, daca acesta depaseste cuantumul penalitatilor." },
    ]);
    sectionNum++;
    articleNum++;
  }

  // LEGEA APLICABILA SI LITIGII
  {
    const roman = toRoman(sectionNum);
    let instantaText = instantaLabels[data.instanta] || instantaLabels.dezvaluie;
    if (data.bilateral) {
      if (data.instanta === "dezvaluie") instantaText = "instantele judecatoresti competente de la sediul/domiciliul Primei Parti";
      if (data.instanta === "primeste") instantaText = "instantele judecatoresti competente de la sediul/domiciliul Celei de-a Doua Parti";
    }

    pdf.section(`${roman}. LEGEA APLICABILA SI LITIGII`);
    pdf.pMixed([
      { text: `${articleNum}.1. `, bold: true },
      { text: "Prezentul acord este guvernat de legea romana." },
    ]);
    pdf.pMixed([
      { text: `${articleNum}.2. `, bold: true },
      { text: "Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de " },
      { text: instantaText, bold: true },
      { text: "." },
    ]);
    sectionNum++;
    articleNum++;
  }

  // DISPOZITII FINALE
  {
    const roman = toRoman(sectionNum);
    pdf.section(`${roman}. DISPOZITII FINALE`);
    pdf.pMixed([
      { text: `${articleNum}.1. `, bold: true },
      { text: "Orice modificare a prezentului acord se face prin act aditional semnat de ambele Parti." },
    ]);
    pdf.pMixed([
      { text: `${articleNum}.2. `, bold: true },
      { text: "Prezentul acord s-a incheiat in 2 exemplare originale, cate unul pentru fiecare Parte." },
    ]);
  }

  // Signatures
  pdf.signatures(
    { role: data.bilateral ? "PRIMA PARTE" : "PARTEA CARE DEZVALUIE", name: getPartyName(data, "partea1"), date: signDate },
    { role: data.bilateral ? "A DOUA PARTE" : "PARTEA CARE PRIMESTE", name: getPartyName(data, "partea2"), date: signDate },
  );

  pdf.save(filename);
}

function toRoman(num) {
  const map = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let result = "";
  for (const [value, numeral] of map) {
    while (num >= value) { result += numeral; num -= value; }
  }
  return result;
}
